import { DefaultLabelFormatterCallbackParams } from "echarts";
import { LineChart, LineSeriesOption } from "echarts/charts";
import {
  GridComponent,
  GridComponentOption,
  TitleComponentOption,
  TooltipComponent,
  TooltipComponentOption,
} from "echarts/components";
import * as echarts from "echarts/core";
import { ComposeOption } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import ReactECharts from "echarts-for-react";
import { TFunction } from "i18next";
import { useRef } from "react";
import { renderToString } from "react-dom/server";
import { useTranslation } from "react-i18next";

import ChartTooltip from "@/components/ui/ChartTooltip";
import {
  TCarbonUnit,
  TScenarioProjections,
  TTrajectoryScenario,
} from "@/types";
import { formatCarbon } from "@/utils/carbon";
import { encodeSvgToDataURI, SYMBOL_SVG } from "@/utils/chart";

import colors from "../../../../colors";
import ScenarioProjectionsChartLegend, {
  ScenarioProjectionsChartLegendRef,
} from "./ScenarioProjectionsChartLegend";

echarts.use([GridComponent, TooltipComponent, LineChart, CanvasRenderer]);

export type ECOption = ComposeOption<
  | LineSeriesOption
  | TitleComponentOption
  | TooltipComponentOption
  | GridComponentOption
>;

export const SCENARIO_COLORS: Record<TTrajectoryScenario, string> = {
  normal: "#F93A55",
  reduced: "#20B2AA",
  sbti: "#4B50F7",
};

function optionFactory(
  scenarioProjections: TScenarioProjections,
  t: TFunction
): ECOption {
  const years = Object.keys(Object.values(scenarioProjections)[0]);

  return {
    textStyle: {
      fontFamily: "Poppins",
    },
    grid: {
      left: "3%",
      right: "3%",
      bottom: "0%",
      top: "10%",
      containLabel: true,
    }, // Configure the grid
    xAxis: {
      type: "category",
      boundaryGap: false,
      data: years,
      axisLine: {
        show: true,
        lineStyle: { color: colors["grayscale"]["x-light"] },
      },
      axisLabel: { color: colors["grayscale"].DEFAULT },
    },
    yAxis: {
      type: "value",
      name: t("common:unit.tCO2e"),
      nameTextStyle: { color: colors["grayscale"].DEFAULT, align: "right" },
      nameGap: 16,
      axisLine: {
        show: true,
        lineStyle: { color: colors["grayscale"]["x-light"] },
      },
      axisLabel: {
        color: colors["grayscale"].DEFAULT,
        formatter: (value) => t("formatter:number", { value }),
      },
      splitLine: {
        lineStyle: {
          type: "dashed",
        },
      },
    },
    tooltip: {
      borderWidth: 0,
      padding: 0,
      extraCssText: "border-radius: 8px;",
      formatter: ({ name, data }: DefaultLabelFormatterCallbackParams) => {
        const { formattedCarbon, unit } = formatCarbon(
          t,
          data as number,
          TCarbonUnit.tCO2e
        );
        return renderToString(
          <ChartTooltip
            seriesName={name}
            valueToDisplay={formattedCarbon}
            unit={t(`common:unit.${unit}`)}
          />
        );
      },
    } as TooltipComponentOption,
    series: (
      Object.keys(scenarioProjections) as Array<TTrajectoryScenario>
    ).map((scenario) => {
      const color = SCENARIO_COLORS[scenario];

      return {
        name: scenario,
        type: "line",
        smooth: true,
        symbol: encodeSvgToDataURI(SYMBOL_SVG, color),
        symbolSize: 12,
        lineStyle: { color, width: 1 },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            {
              offset: 0,
              // here {color} is the hexadecimal code of the color (RGB format), followed by an alpha value, it becomes an RGBA format
              color: `${color}4D`, // opacity 77% (4D base-16 = 77 base-10)
            },
            {
              offset: 1,
              color: `${color}0D`, // opacity 13% (0D base-16 = 13 base-10)
            },
          ]),
        },
        emphasis: {
          focus: "series",
        },
        triggerLineEvent: true,
        data: Object.values(
          scenarioProjections[scenario as TTrajectoryScenario]
        ),
      };
    }),
  } satisfies ECOption;
}

type ScenarioProjectionsChartProps = {
  scenarioProjections: TScenarioProjections;
};

function ScenarioProjectionsChart({
  scenarioProjections,
}: ScenarioProjectionsChartProps) {
  const { t } = useTranslation(["common", "formatter"]);
  const echartsRef = useRef<ReactECharts>(null);
  const legendRef = useRef<ScenarioProjectionsChartLegendRef>(null);
  const option = optionFactory(scenarioProjections, t);

  const handleChartHover = (scenario: TTrajectoryScenario | undefined) => {
    legendRef.current?.highlight(scenario);
  };

  const handleLegendHover = (scenario: TTrajectoryScenario | undefined) => {
    if (scenario) {
      echartsRef.current?.getEchartsInstance().dispatchAction({
        type: "highlight",
        seriesName: scenario,
      });
    } else {
      echartsRef.current?.getEchartsInstance().dispatchAction({
        type: "downplay",
      });
    }
  };

  return (
    <div className="gap-y-xs flex flex-col">
      <ReactECharts
        echarts={echarts}
        option={option}
        ref={echartsRef}
        onEvents={{
          mouseover: ({ seriesName }: { seriesName: string }) =>
            handleChartHover(seriesName as TTrajectoryScenario),
          mouseout: () => handleChartHover(undefined),
        }}
      />
      <ScenarioProjectionsChartLegend
        scenarioProjections={scenarioProjections}
        ref={legendRef}
        onHover={handleLegendHover}
      />
    </div>
  );
}

export default ScenarioProjectionsChart;
